import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteTenant, type Tenant, type TenantDeletion } from "./tenantsApi";
import { deleteSectionState, deletionErrorKey, slugConfirmed } from "./tenantDeletion";
import { ApiError, SessionExpiredError } from "@web/lib/api";
import { Modal } from "@web/ui";

// "POISTA DEMOTENANTTI" — the prospect did not buy, and their demo tenant goes for good (task dc216a05,
// owner decision 1.11.2026, ADR-0023).
//
// WHY IT IS BESIDE THE FREEZE AND NOT INSIDE IT. They are the two ends of one lifecycle and they are
// opposites: the freeze deletes NOTHING, which is the whole reason its confirmation can state its effects
// as facts and offer a thaw. This one is the only act on this console that cannot be undone, so it is its
// own section, wearing the critical edge, below the one that gets the operator there.
//
// THE VISIBILITY RULE IS THE FIRST GUARD AND IT IS NOT COSMETIC (see `deleteSectionState`):
//
//   * A tenant that is not a demo tenant renders NOTHING — not a disabled button. Deleting a paying
//     customer's tenant is not an operation this console has, and a greyed-out control over a customer row
//     would suggest it is one press from existing. The server refuses it too (409 `tenant_not_demo`); this
//     is the console declining to offer it.
//   * A demo tenant that is still LIVE renders the hint and no button. The server refuses that as well
//     (409 `tenant_not_suspended`) — freezing first is what stops the tenant issuing tokens and running its
//     background sweeps, so the purge's single transaction is not racing the tenant's own writers.
//
// THE DIALOG SAYS WHAT GOES AND WHAT DOES NOT, and the second half is the one that needs the care (§6.2):
// the tenant's rows, ITS AUDIT HISTORY, its files, and the user accounts of its own people WHOSE ONLY
// MEMBERSHIP WAS THIS TENANT — an Incedo account or a consultant with a second organisation is kept, and
// the ledger never records it. What the copy must not promise is that the backups are erased: the 30-day
// PITR copy is settled by the Transit crypto-shred, which is exactly why an unconfirmed shred is reported
// `incomplete` and never `done` (§6.4, ADR-0013).
//
// THE SLUG IS TYPED ON EVERY PRESS, retries included (§7 item 3) — which is why this component has no
// "already confirmed" state to carry. For the retry the typing happens again, on the tenant LIST, where the
// deletion is still visible (`TenantDeletionsSection`): by then there is no `tenants` row to compare
// against, and the server matches what was typed against the ledger's `tenant_slug` or the `TENANT_DELETED`
// audit row's.
//
// A RESULT IS NOT KEPT HERE, and that is the lesson `TenantShredOutstanding` already paid for on the
// conversion. A deletion that answered 200 has COMMITTED: there is no tenant left for this page to re-read,
// so the page that holds this section is about to stop describing anything. The outstanding stores and the
// retry therefore belong to the DIRECTORY, and `onDeleted` hands the result up for the page to navigate
// with. What stays here are the endings that CHANGED NOTHING and so leave this section standing: a refusal
// and a transport failure.

/** The confirmation's own state. Absent means the section is in its resting, no-dialog state. */
type Dialog = { confirm: string; submitting: boolean; error: string | null };

const EMPTY_DIALOG: Dialog = { confirm: "", submitting: false, error: null };

/**
 * What the confirmation lists, as i18n keys. A list and not one paragraph: an operator reading a
 * confirmation reads the bullets, and the two that matter most are the one naming the AUDIT HISTORY (the
 * one thing on this platform that is otherwise append-only for ever) and the one bounding which user
 * accounts go.
 */
const DELETE_EFFECTS = [
  "tenants.delete.effectData",
  "tenants.delete.effectAudit",
  "tenants.delete.effectAccounts",
  "tenants.delete.effectFiles",
  "tenants.delete.effectDemoOnly",
  // Last, and it is the bullet that says what this deletion does NOT do: the backups are not wiped. The
  // 30-day PITR copy is settled by the Transit crypto-shred instead, which is precisely why an unconfirmed
  // shred is reported `incomplete` and never `done` (§6.4, ADR-0013) — so promising erased backups here
  // would be a claim the operation deliberately does not make.
  "tenants.delete.effectBackups",
] as const;

export function TenantDeleteSection({
  tenant,
  headingId,
  onDeleted,
}: {
  tenant: Tenant;
  /** The id of this section's own <h2> — the page owns the heading, this owns everything under it. */
  headingId: string;
  /**
   * The tenant is gone. The page says so and leaves — there is nothing here left to re-read, and the
   * outstanding obligations outlive this surface (see the header comment).
   */
  onDeleted: (result: TenantDeletion) => void;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const confirmRef = useRef<HTMLInputElement>(null);

  const state = deleteSectionState(tenant);

  const onSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (!dialog || dialog.submitting) return;

      // Functional updates throughout: what the operator typed is the part of this state that must not be
      // overwritten by a stale closure, and a refusal has to leave the dialog exactly as they left it.
      setDialog((d) => d && { ...d, submitting: true, error: null });
      deleteTenant(tenant.id, { slug: dialog.confirm })
        .then((result) => {
          setDialog(null);
          onDeleted(result);
        })
        .catch((err: unknown) => {
          if (err instanceof SessionExpiredError) return;
          const key =
            err instanceof ApiError
              ? deletionErrorKey(err.status, err.problem)
              : "tenants.delete.error";
          setDialog((d) => d && { ...d, submitting: false, error: t(key) });
        });
    },
    [dialog, tenant.id, onDeleted, t],
  );

  // Nothing at all for a tenant that is not a demo tenant — see the header comment.
  if (state === "none") return null;

  const titleId = "tenant-delete-title";
  const bodyId = "tenant-delete-body";
  const confirmed = slugConfirmed(dialog?.confirm ?? "", tenant.slug);

  return (
    <>
      <h2 id={headingId}>{t("tenants.delete.title")}</h2>

      {state === "needsFreeze" ? (
        <p className="tenants__slug-status" data-testid="tenant-delete-needs-freeze">
          {t("tenants.delete.needsFreeze")}
        </p>
      ) : (
        <>
          <p className="freeze__intro">{t("tenants.delete.intro")}</p>
          <div className="tenant-section__actions">
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setDialog(EMPTY_DIALOG)}
              data-testid="tenant-delete-open"
            >
              {t("tenants.delete.open")}
            </button>
          </div>
        </>
      )}

      {dialog && (
        <Modal
          role="alertdialog"
          className="freeze__dialog"
          labelledBy={titleId}
          describedBy={bodyId}
          testId="tenant-delete-dialog"
          onClose={() => setDialog(null)}
          closeDisabled={dialog.submitting}
          initialFocus={confirmRef}
        >
          <h2 id={titleId}>{t("tenants.delete.confirmTitle", { tenant: tenant.slug })}</h2>
          <ul id={bodyId} className="freeze__effects">
            {DELETE_EFFECTS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ul>

          <form className="form" onSubmit={onSubmit} noValidate>
            <label htmlFor="tenant-delete-confirm">
              {t("tenants.delete.confirmLabel", { tenant: tenant.slug })}
            </label>
            <input
              id="tenant-delete-confirm"
              ref={confirmRef}
              type="text"
              value={dialog.confirm}
              onChange={(e) => setDialog((d) => d && { ...d, confirm: e.target.value })}
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              disabled={dialog.submitting}
              data-testid="tenant-delete-confirm"
            />

            {dialog.error && (
              <p className="field-error" role="alert" data-testid="tenant-delete-dialog-error">
                {dialog.error}
              </p>
            )}

            <div className="modal__actions">
              <button
                type="button"
                className="btn"
                onClick={() => setDialog(null)}
                disabled={dialog.submitting}
                data-testid="tenant-delete-cancel"
              >
                {t("tenants.cancel")}
              </button>
              <button
                type="submit"
                className="btn btn--danger"
                disabled={!confirmed || dialog.submitting}
                data-testid="tenant-delete-submit"
              >
                {dialog.submitting ? t("tenants.delete.deleting") : t("tenants.delete.submit")}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
